import { useEffect } from 'react'
import { IoInformationCircle } from 'react-icons/io5'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'

import {
  ImagePreview,
  useImagePreview
} from 'src/providers/ImagePreviewProvider'

import { Divider } from 'src/components/atoms/Divider'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { CopyLink } from 'src/components/atoms/Link/CopyLink'
import { SanityImage } from 'src/components/atoms/SanityImage'
import { Tooltip } from 'src/components/atoms/Tooltip'
import { Truncated } from 'src/components/atoms/Truncated'
import {
  Heading1,
  Heading4,
  Heading5,
  ParagraphNoSpace
} from 'src/components/atoms/Typography'
import {
  FullWidthContainer,
  FullWidthContainerBackgroundColor
} from 'src/components/layouts/Container'
import { Content } from 'src/components/molecules/sanity/Content'

import { NativeTreeSpecies } from 'src/config/generated/sanitySchema'
import { nativeTreeSpeciesPath, nativeTreesPath } from 'src/config/paths'
import { NotFound } from 'src/pages/404/404'
import { NativeTreeSpeciesBySlugQueryResult } from 'src/server/router/sanity/groq/nativeTrees/types'
import { ArrayElement } from 'src/types/common'
import { getSanityImageUrl } from 'src/utils/sanity'

import { traits } from '../NativeTrees/TraitsSection/traits'

export interface Props {
  species: NativeTreeSpeciesBySlugQueryResult
}

type ConservationStatus = NonNullable<NativeTreeSpecies['conservationStatus']>

export const NativeTree = ({ species }: Props) => {
  const { setImageUrls, show } = useImagePreview()

  const identificationParameters: {
    [key in ArrayElement<
      NonNullable<NativeTreeSpecies['identification']>
    >['characteristic']]: { text: string; icon: string }
  } = {
    flowersAndFruit: {
      text: 'Flowers and fruit',
      icon: '/img/icons/TTC_icon_MeetTheTrees_flower.svg'
    },
    heightAndShape: {
      text: 'Height and shape',
      icon: '/img/icons/TTC_icon_MeetTheTrees_tree.svg'
    },
    leavesAndBark: {
      text: 'Leaves and bark',
      icon: '/img/icons/TTC_icon_MeetTheTrees_leaf.svg'
    }
  }

  const conservationStatus: {
    [key in ConservationStatus]: {
      text: string
      color: string
    }
  } = {
    atRisk: { text: 'At risk', color: 'bg-sunsetGold' },
    notThreatened: { text: 'Not threatened', color: 'bg-brightGreen' },
    threatened: { text: 'Threatened', color: 'bg-darkAlizarin' }
  }

  useEffect(() => {
    if (species) {
      setImageUrls([getSanityImageUrl(species.image)])
    }

    // adding setImageUrls as a dependency creates a loop, so ignore the warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species])

  if (!species) return <NotFound />

  return (
    <FullWidthContainer
      backgroundColor={FullWidthContainerBackgroundColor.linen}
      className='px-5! md:pt-10! pt-5!'
    >
      <div className='flex flex-col gap-5 md:gap-10'>
        <div className='flex flex-col gap-[10px]'>
          <div className='flex flex-row items-center gap-[10px]'>
            <div className='hidden md:block w-[50px] h-[50px] relative overflow-hidden rounded-full'>
              <SanityImage
                className='object-cover'
                alt={species.name}
                source={species.avatar}
                width={50}
                height={50}
              />
            </div>
            <Heading1 className='m-0!'>{species.name}</Heading1>
          </div>
          <div className='flex flex-col md:flex-row justify-between items-end gap-[10px] md:gap-5'>
            <span className='text-[16px] leading-[24px] md:text-[20px] md:leading-[30px] font-medium self-start'>
              {species.scientificName}
            </span>
            <CopyLink />
          </div>
        </div>
        <div className='flex flex-col md:flex-row gap-10'>
          <div className='flex-1 flex flex-col md:flex-col gap-10'>
            <div className='flex flex-col xl:flex-row min-h-[642px]'>
              <div className='relative xl:w-[318px] xl:h-full w-full h-[460px]'>
                <SanityImage
                  alt={species.name}
                  source={species.image}
                  fill
                  className='object-cover cursor-pointer'
                  onClick={() => show(getSanityImageUrl(species.image))}
                />
              </div>
              <div className='flex-1 flex flex-col bg-white p-5 md:p-10 gap-5'>
                <Heading4 className='m-0!'>Description</Heading4>
                <div className='block xl:hidden'>
                  <Truncated
                    className='font-medium'
                    moreText='show more'
                    lessText='show less'
                  >
                    <Content body={species.about} />
                  </Truncated>
                </div>
                <ParagraphNoSpace className='hidden xl:block font-medium'>
                  <Content body={species.about} />
                </ParagraphNoSpace>
              </div>
            </div>
            {species.identification?.length ? (
              <div className='flex flex-col bg-aquaSpring p-5 md:p-10 gap-5'>
                <Heading4 className='m-0!'>
                  How to identify {species.name}
                </Heading4>
                <div className='flex flex-col xl:flex-row gap-5'>
                  {species.identification?.map(characteristic => (
                    <div
                      key={characteristic._key}
                      className='md:flex-1 flex-auto flex flex-col bg-white gap-2 rounded-[10px] p-5 min-h-[130px] max-h-[250px] overflow-hidden'
                    >
                      <div className='flex flex-row gap-[10px]'>
                        <Image
                          src={
                            identificationParameters[
                              characteristic.characteristic
                            ].icon
                          }
                          alt={characteristic.characteristic}
                          width={24}
                          height={24}
                        />
                        <span className='text-[18px] leading-[22px] font-bold'>
                          {
                            identificationParameters[
                              characteristic.characteristic
                            ].text
                          }
                        </span>
                      </div>
                      <ParagraphNoSpace className='font-medium'>
                        {characteristic.description}
                      </ParagraphNoSpace>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className='flex flex-col w-full md:max-w-[290px]'>
            <div className='bg-mostlyGreen px-10 py-5'>
              <Heading4 className='text-white m-0!'>Facts</Heading4>
            </div>
            <div className='flex flex-col bg-white p-5 md:p-10 gap-5'>
              {species.habitat ? (
                <>
                  <div className='flex flex-col gap-[10px]'>
                    <Heading5 className='m-0!'>Habitat</Heading5>
                    <ParagraphNoSpace className='font-medium'>
                      {species.habitat}
                    </ParagraphNoSpace>
                  </div>
                  <Divider className='m-0!' />
                </>
              ) : null}
              {species.conservationStatus ? (
                <>
                  <div className='flex flex-col gap-[10px]'>
                    <Heading5 className='m-0!'>
                      Conservation status{' '}
                      <Tooltip overlay='Info about conservation status'>
                        <IoInformationCircle className='inline-block align-text-bottom text-mostlyGreen w-[18px] h-[18px]' />
                      </Tooltip>
                    </Heading5>
                    {species.conservationStatus ? (
                      <div className='flex flex-row gap-[5px] items-center'>
                        <div
                          className={`rounded-full w-5 h-5 ml-1 mr-3 ${
                            conservationStatus[species.conservationStatus].color
                          }`}
                        />
                        <span className='text-base font-medium'>
                          {conservationStatus[species.conservationStatus].text}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <Divider className='m-0!' />
                </>
              ) : null}
              <div className='flex flex-col gap-[10px]'>
                <Heading5 className='m-0!'>Traits</Heading5>
                <div className='flex flex-col gap-5'>
                  {species.traits?.map(t => {
                    const trait = traits[t as keyof typeof traits]
                    if (!trait) return null
                    return (
                      <div
                        key={trait.name}
                        className='flex flex-row gap-[10px] items-start'
                      >
                        <Image
                          src={trait.iconSrc}
                          alt={trait.name}
                          width={30}
                          height={30}
                        />
                        <ParagraphNoSpace className='font-medium'>
                          {trait.description}
                        </ParagraphNoSpace>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='flex flex-row justify-between'>
          {species.lastTree ? (
            <InternalLink
              className='align-middle'
              href={nativeTreeSpeciesPath({
                routeParams: {
                  slug: species.lastTree?.slug.current
                }
              })}
            >
              <MdChevronLeft size={20} className='inline' />
              Previous
            </InternalLink>
          ) : (
            <div />
          )}
          <InternalLink href={nativeTreesPath}>All trees</InternalLink>
          {species.nextTree ? (
            <InternalLink
              className='align-middle'
              href={nativeTreeSpeciesPath({
                routeParams: {
                  slug: species.nextTree?.slug.current
                }
              })}
            >
              Next <MdChevronRight size={20} className='inline' />
            </InternalLink>
          ) : (
            <div />
          )}
        </div>
      </div>
      <ImagePreview />
    </FullWidthContainer>
  )
}
